/**
 * Typing Animation Component
 * Adapted from Magic UI TypingAnimation
 * Creates a typewriter effect that types out text character by character
 */

export class TypingAnimation {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      speed: options.speed || 50, // milliseconds per character
      cursor: options.cursor !== false, // show cursor
      cursorChar: options.cursorChar || '|',
      onComplete: options.onComplete || null,
      onStart: options.onStart || null,
      ...options,
    };
    
    this.text = element.textContent || element.innerText || '';
    this.currentIndex = 0;
    this.isTyping = false;
    this.cursorElement = null;
    
    this.init();
  }
  
  init() {
    // Clear the element
    this.element.textContent = '';
    this.element.style.display = 'inline-block';
    
    // Create cursor if enabled
    if (this.options.cursor) {
      this.cursorElement = document.createElement('span');
      this.cursorElement.className = 'typing-cursor';
      this.cursorElement.textContent = this.options.cursorChar;
      this.cursorElement.style.opacity = '1';
      this.cursorElement.style.display = 'none'; // Hidden by default
      this.element.appendChild(this.cursorElement);
    }
  }
  
  start() {
    if (this.isTyping) return;
    
    this.isTyping = true;
    this.currentIndex = 0;
    
    if (this.options.onStart) {
      this.options.onStart();
    }
    
    this.type();
  }
  
  type() {
    if (this.currentIndex < this.text.length) {
      // Insert character before cursor
      const char = this.text[this.currentIndex];
      const textNode = document.createTextNode(char);
      
      if (this.cursorElement) {
        this.element.insertBefore(textNode, this.cursorElement);
      } else {
        this.element.appendChild(textNode);
      }
      
      this.currentIndex++;
      
      // Continue typing
      setTimeout(() => {
        this.type();
      }, this.options.speed);
    } else {
      // Typing complete
      this.isTyping = false;
      
      if (this.options.onComplete) {
        this.options.onComplete();
      }
    }
  }
  
  reset() {
    this.currentIndex = 0;
    this.isTyping = false;
    this.element.textContent = '';
    this.init();
  }
  
  setText(newText) {
    this.text = newText;
    this.reset();
  }
  
  setProgress(progress) {
    // Set typing progress (0 to 1)
    const targetIndex = Math.floor(progress * this.text.length);
    
    if (targetIndex !== this.currentIndex) {
      // Update displayed text
      const displayedText = this.text.substring(0, targetIndex);
      
      // Remove all child nodes except cursor
      const nodesToRemove = Array.from(this.element.childNodes).filter(
        node => node !== this.cursorElement
      );
      nodesToRemove.forEach(node => node.remove());
      
      // Add new text
      if (displayedText) {
        const textNode = document.createTextNode(displayedText);
        if (this.cursorElement) {
          this.element.insertBefore(textNode, this.cursorElement);
          // Show cursor when typing
          if (targetIndex < this.text.length) {
            this.cursorElement.style.display = 'inline-block';
          } else {
            this.cursorElement.style.display = 'none';
          }
        } else {
          this.element.appendChild(textNode);
        }
      } else if (this.cursorElement) {
        // Hide cursor when no text
        this.cursorElement.style.display = 'none';
      }
      
      this.currentIndex = targetIndex;
    }
  }
  
  getCurrentText() {
    // Get the currently displayed text (without cursor)
    return this.text.substring(0, this.currentIndex);
  }
  
  complete() {
    // Instantly complete the typing
    this.setProgress(1);
    this.isTyping = false;
  }
}

/**
 * Initialize typing animations for elements with data-typing attribute
 */
export function initTypingAnimations() {
  const elements = document.querySelectorAll('[data-typing]');
  
  elements.forEach((element) => {
    const speed = parseInt(element.dataset.typingSpeed) || 50;
    const cursor = element.dataset.typingCursor !== 'false';
    
    const typing = new TypingAnimation(element, {
      speed,
      cursor,
    });
    
    // Store instance on element for external control
    element.typingInstance = typing;
  });
}

